import { isRegisterWithImmatureNeighbourAtom } from 'app/atoms';
import { userInfoAtom } from 'app/atoms/auth';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { Event, track } from 'app/lib/analytics';
import { recordError } from 'app/lib/monitoring';
import { useClosestOffersByTag } from 'app/network/listing';
import { useUpsertOnePreference } from 'app/network/preferences';
import type { SpecificNavigationProp } from 'app/types/navigation';
import type { SharerType } from 'app/types/preferences';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRef, useState } from 'react';

export function useChooseSharerTypeFlow(navigation: SpecificNavigationProp<'choose-sharer-type'>) {
  const { goToNext } = useOnboarding();
  const userInfo = useAtomValue(userInfoAtom);
  const hasAcceptedGuidelines = !!userInfo?.acceptedGuidelineDate;
  const { mutateAsync: upsertSharerTypePreferences } = useUpsertOnePreference('sharerType');
  const [isPending, setIsPending] = useState(false);
  const isSubmitting = useRef(false);
  const [selectedSharerType, setSelectedSharerType] = useState<SharerType | null>(null);
  const { refetch } = useClosestOffersByTag(undefined, { enabled: false });
  const setIsRegisterWithImmatureNeighbour = useSetAtom(isRegisterWithImmatureNeighbourAtom);

  const saveSharerTypePreferences = async () => {
    if (!selectedSharerType || isSubmitting.current) return;

    isSubmitting.current = true;
    setIsPending(true);

    try {
      const query = await refetch();
      if (query.isError) {
        recordError(query.error);
      } else {
        let offerCount = 0;
        if (query.data) {
          offerCount = query.data.length;
        }
        const hasEnoughOffers = offerCount >= 10;
        setIsRegisterWithImmatureNeighbour(!hasEnoughOffers);

        await upsertSharerTypePreferences(selectedSharerType);
        track(Event.ChooseSharerType, { selected: selectedSharerType });
        if (hasAcceptedGuidelines) {
          navigation.goBack();
        } else {
          goToNext();
        }
      }
    } catch (error) {
      recordError(error);
    }
    isSubmitting.current = false;
    setIsPending(false);
  };

  const onSelect = (type: SharerType) => {
    if (isSubmitting.current) return;
    setSelectedSharerType(type);
  };

  return {
    isPending,
    isSharerTypeSelected: !!selectedSharerType,
    onSelect,
    saveSharerTypePreferences,
    selectedSharerType,
    userInfo,
  };
}
